import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { DEFAULT_HOME } from '@/auth/routes'
import { formatClock } from '@/lib/format'
import { useTenants } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { InlineError } from '@/ui/states'
import { useOnboarding, useWizard } from './context'
import { connectionFor, detailOf, metaFor, notConfigured, pathFor } from './steps'

/**
 * The finished screen — S-D2's `done`.
 *
 * Completion is claimed by the **server**: this screen posts `/complete` on
 * arrival rather than assuming the last step finished the wizard, because a
 * tenant can reach step 6 with steps 2 to 4 skipped and the required-step rule
 * lives in one place (`app/onboarding/steps.py`). Idempotent, so a reload does
 * not move the timestamp.
 *
 * "Waiting on you" lists what was skipped, and *Finish the skipped steps*
 * reopens the wizard on the first of them. Without that the only way back in
 * would be to create a second organisation.
 */
export function Finished(): JSX.Element {
  const state = useWizard()
  const { adopt } = useOnboarding()
  const navigate = useNavigate()

  const complete = useMutation(() => call(() => api.POST('/api/v1/onboarding/complete', {})))
  const reopen = useMutation(() => call(() => api.POST('/api/v1/onboarding/reopen', {})))

  // Parity P-ONBD-01. `onboarding.html#done` draws the second tile as
  // *"Tonight, 04:00 — First content briefs written"*: a clock time. The
  // honest source for one is the site's run schedule (`next_run_at`), which
  // exists only once a schedule is switched on — for most tenants standing
  // here it is off, and the tile then says when without inventing a clock
  // (ONB-21). Read, never derived: the prediction is the server's.
  const siteId = state.site?.id ?? null
  const schedule = useQuery(
    (signal) =>
      siteId === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/run-schedules/{site_id}', {
              params: { path: { site_id: siteId } },
              signal,
            }),
          ),
    [siteId],
  )
  const timeZone = useTenants().active?.tenant.timezone ?? 'Asia/Kolkata'
  const nextRunAt = schedule.state.data?.next_run_at ?? null

  const claimed = useRef(false)
  useEffect(() => {
    if (claimed.current || state.completed_at) return
    claimed.current = true
    void (async () => {
      const result = await complete.run(undefined)
      if (result.ok) adopt(result.data)
    })()
  }, [state.completed_at, complete, adopt])

  async function onReopen(): Promise<void> {
    const result = await reopen.run(undefined)
    if (result.ok) {
      adopt(result.data)
      void navigate(pathFor(result.data.current_step))
    }
  }

  // Not `progress.outstanding` — a skipped step is settled for the wizard and
  // outstanding for the tenant, and this panel is the tenant's view. See
  // `notConfigured`.
  // Still the count the footer's *Finish the skipped steps* button reads.
  const outstanding = notConfigured(state)
    .map((row) => metaFor(row.step))
    .filter((meta) => meta !== null)

  /**
   * ONB-20. S-D2's *Waiting on you* names concrete items — "DKIM record ·
   * LinkedIn skipped" — where the app listed step names, "Sender · LinkedIn".
   *
   * The two are not the same list. A **settled** sender whose DKIM record has
   * not been published is a step the wizard is finished with and a thing the
   * tenant still has to do, and it is the one entry on this panel that costs
   * money to miss: outreach from an unauthenticated domain lands in spam. It
   * appeared nowhere, because every reading of "what is left" ran off the step
   * rows. The DNS rows are read the way `SenderStep` reads them.
   *
   * A skipped step reads "LinkedIn skipped" rather than bare "LinkedIn": the
   * panel is a sentence about what is outstanding, and a noun on its own does
   * not say whether it failed, was skipped, or was never reached.
   */
  const unverified = dnsShortfall(state)
  const waiting = [
    ...unverified,
    // `notConfigured` is every step that is not `done` — which is skipped,
    // never-reached, *and failed*. Labelling all three "skipped" tells somebody
    // whose Search Console consent was declined that they chose to pass on it.
    // Found reverifying: the mockup's example happens to contain only skipped
    // steps, so matching its string exactly was not the same as being right.
    ...notConfigured(state)
      .map((row) => {
        const meta = metaFor(row.step)
        if (!meta) return null
        return row.status === 'failed' ? `${meta.short} did not connect` : `${meta.short} skipped`
      })
      .filter((line) => line !== null),
  ]

  return (
    <div className="app-wiz" style={{ maxWidth: '44rem' }}>
      <section className="ar-card">
        <div className="ar-card__body">
          <InlineError failure={complete.state.failure ?? reopen.state.failure} />

          <div className="ar-empty">
            <div
              className="ar-empty__icon"
              style={{ background: 'var(--ar-success-bg)', color: 'var(--ar-success)' }}
              aria-hidden="true"
            >
              ✓
            </div>
            <p className="ar-empty__title">You&rsquo;re set up</p>
            <p className="ar-empty__body">
              Your first audit is running now. We&rsquo;ll email you when the first drafts are ready
              for approval — usually within a day.
            </p>
          </div>

          <div className="ar-grid ar-grid--3" style={{ marginTop: 'var(--ar-space-6)' }}>
            <div className="ar-panel">
              <strong>Running now</strong>
              <p
                className="ar-muted"
                style={{ margin: 'var(--ar-space-2) 0 0', fontSize: 'var(--ar-text-body-sm-size)' }}
              >
                Technical audit · {state.keywords.filter((row) => row.enabled).length} keywords
                tracked
              </p>
            </div>
            {/* ONB-21. **No clock time**, which the parity row offers as the
                alternative to reading the tenant's own schedule. "Tonight, 04:00"
                was a hardcoded string: it belonged to no tenant, it was wrong for
                anyone whose run trigger differed, and every tenant standing on
                this screen has just finished the wizard and has no `run_schedules`
                row at all — so the honest value would be absent for exactly this
                audience. Saying when it happens without inventing a clock is what
                is left, and it is true for everyone. */}
            <div className="ar-panel">
              <strong>
                {nextRunAt === null
                  ? 'Next up'
                  : `${whenWord(nextRunAt, new Date(), timeZone)}, ${formatClock(nextRunAt, timeZone)}`}
              </strong>
              <p
                className="ar-muted"
                style={{ margin: 'var(--ar-space-2) 0 0', fontSize: 'var(--ar-text-body-sm-size)' }}
              >
                {nextRunAt === null
                  ? 'First content briefs, on your next scheduled run'
                  : 'First content briefs written'}
              </p>
            </div>
            <div className="ar-panel">
              <strong>Waiting on you</strong>
              <p
                className="ar-muted"
                style={{ margin: 'var(--ar-space-2) 0 0', fontSize: 'var(--ar-text-body-sm-size)' }}
              >
                {waiting.length === 0 ? 'Nothing — every step is done' : waiting.join(' · ')}
              </p>
            </div>
          </div>
        </div>

        <div className="ar-card__footer">
          <Button
            tone="primary"
            onClick={() => {
              void navigate(DEFAULT_HOME)
            }}
          >
            Go to my dashboard
          </Button>
          {outstanding.length > 0 ? (
            <Button
              busy={reopen.pending}
              busyLabel="Reopening setup"
              onClick={() => {
                void onReopen()
              }}
            >
              Finish the skipped steps
            </Button>
          ) : null}
        </div>
      </section>
    </div>
  )
}

/**
 * DNS records the sender still needs published, as the panel names them.
 *
 * Defensive in the same way `SenderStep.dnsRecords` is and for the same reason:
 * the shape belongs to S-B18, and a malformed row on the finished screen should
 * cost that row rather than the screen a tenant has just arrived at.
 */
function dnsShortfall(state: ReturnType<typeof useWizard>): string[] {
  const rows = detailOf(connectionFor(state, 'sender_identity')).dns
  if (!Array.isArray(rows)) return []
  return rows
    .filter((row): row is { kind: string } => {
      const record = row as { kind?: unknown; verified?: unknown }
      return typeof record.kind === 'string' && record.verified !== true
    })
    .map((row) => `${row.kind} record`)
}

/**
 * The day half of *"Tonight, 04:00"*, in the organisation's zone.
 *
 * *Tonight* for a run in the coming night — this evening, or before six
 * tomorrow morning, which is when the mockup's 04:00 falls; *Today* and
 * *Tomorrow* for daytime runs on those days; the weekday otherwise.
 */
function whenWord(iso: string, now: Date, timeZone: string): string {
  const day = new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'short' })
  const hourOf = (date: Date): number =>
    Number(
      new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: false, timeZone }).format(date),
    )
  const run = new Date(iso)
  const today = day.format(now)
  const tomorrow = day.format(new Date(now.getTime() + 86_400_000))
  const runDay = day.format(run)
  const hour = hourOf(run)
  if ((runDay === today && hour >= 18) || (runDay === tomorrow && hour < 6)) return 'Tonight'
  if (runDay === today) return 'Today'
  if (runDay === tomorrow) return 'Tomorrow'
  return new Intl.DateTimeFormat('en-GB', { weekday: 'long', timeZone }).format(run)
}
